import { useState, useEffect } from 'react';
import { withRouter, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { selectFeatureFlags, selectIsSMSClickWebhookUIFeatureFlagOn } from 'core/userContext';
import { PageHeader } from 'ui/pageHeader';
import { Divider } from 'ui/divider';
import { useWindowSize } from 'utils/hooks/useWindowSize';
import { validateSystemWebhookRequest, fetchSystemWebhooksRequest } from '../actions';
import * as c from '../constants';
import { selectSystemWebhooksList, selectInitialFetchMade } from '../selectors';
import { ActionButtons } from './ActionButtons';
import { EndpointFormField } from './EndpointFormField';
import { StatusFormField } from './StatusFormField';
import { AuthenticationFormField } from './AuthenticationFormField';
import { CustomHttpHeadersFormField } from './CustomHttpHeadersFormField';
import { AuthenticationTokenFormField } from './AuthenticationTokenFormField';
import { TriggerEventGroupsFormField } from './TriggerEventGroupsFormField';
import { FiltersFormField } from './FiltersFormField';
import { EditWebhookForm } from './EditSystemWebhook.styled';
import { generateStateFromWebhookParams, authTypeRequiresToken } from './EditSystemWebhook.utils';
import { SystemWebhookFormState } from '../types';

const EditSystemWebhook = () => {
  const dispatch = useDispatch();
  const { SystemWebhookAdvancedFiltering } = useSelector(selectFeatureFlags);
  const { webhookId = '' } =
    (useRouteMatch(c.EDIT_WEBHOOK_ROUTE)?.params as { webhookId: string }) || {};
  const webhooksList = useSelector(selectSystemWebhooksList);
  const initialFetchMade = useSelector(selectInitialFetchMade);
  const webhookParams = webhooksList[webhookId] || {};

  const showSmsClickAsAnOption = useSelector(selectIsSMSClickWebhookUIFeatureFlagOn);

  const [webhook, setWebhook] = useState<SystemWebhookFormState>(
    generateStateFromWebhookParams(webhookParams),
  );
  const requiresToken = authTypeRequiresToken(webhook.authType);

  const handleSaveWebhook = () /*: WebhookParamProps */ => {
    const saveWebhookParams = {
      authToken: webhook.authToken,
      authType: webhook.authType,
      blastSendEnabled: !!webhook.enabledEvents.blastSendEnabled,
      endpoint: webhook.endpoint,
      enabled: webhook.enabled === c.ENABLED_WEBHOOK.value,
      enabledEvents: Object.keys(webhook.enabledEvents).filter((event) => {
        /* quick and dirty check that:
         * 1. if the SMSClickWebhookUIFeatureFlagOn was ever turned on
         * 2. sms click was selected and saved
         * 3. SMSClickWebhookUIFeatureFlagOn was then turned off (and is now in a turned off state)
         * 4. that saving the webhook will automatically turn smsClick off
         */
        if (!showSmsClickAsAnOption && event === 'smsClick') {
          return false;
        }

        return webhook.enabledEvents[event] && !c.ADDITIONAL_EMAIL_EVENTS.includes(event);
      }),
      headers: webhook.headers,
      id: webhookParams.id,
      projectId: webhookParams.projectId,
      requiresToken,
      triggeredSendEnabled: !!webhook.enabledEvents.triggeredSendEnabled,
      rateLimitPerSecond: webhookParams.rateLimitPerSecond, // BE added rateLimitPerSecond and needs to persist it on edit
      excludeListUpdates: webhook.excludeListUpdates,
    };

    dispatch(validateSystemWebhookRequest({ params: saveWebhookParams }));
  };

  const updateWebhook = (key: string, val: string | string[] | boolean) =>
    setWebhook({
      ...webhook,
      [key]: val,
    });

  /** Set the form to be full width when the window is narrow */
  const { width: windowWith } = useWindowSize();
  const isNarrowWindow = windowWith < 900;

  useEffect(() => {
    if (!initialFetchMade) {
      dispatch(fetchSystemWebhooksRequest());
    }
    setWebhook(generateStateFromWebhookParams(webhookParams));
  }, [initialFetchMade]);

  return (
    <>
      <PageHeader
        breadcrumb={{ content: 'System Webhooks', href: '/integrations/webhooks' }}
        documentation={{
          href: 'https://support.iterable.com/hc/en-us/articles/208013936-System-Webhooks',
          content: 'Documentation',
        }}
      >
        Edit system webhook
      </PageHeader>

      <Divider />

      <EditWebhookForm useFullWidth={isNarrowWindow}>
        <EndpointFormField updateWebhook={updateWebhook} webhook={webhook} />

        <StatusFormField updateWebhook={updateWebhook} webhook={webhook} />

        <AuthenticationFormField updateWebhook={updateWebhook} webhook={webhook} />
        {requiresToken && (
          <AuthenticationTokenFormField updateWebhook={updateWebhook} webhook={webhook} />
        )}

        {SystemWebhookAdvancedFiltering && (
          <FiltersFormField updateWebhook={updateWebhook} webhook={webhook} />
        )}

        <CustomHttpHeadersFormField updateWebhook={updateWebhook} webhook={webhook} />

        <TriggerEventGroupsFormField updateWebhook={updateWebhook} webhook={webhook} />

        <ActionButtons handleSave={handleSaveWebhook} />
      </EditWebhookForm>
    </>
  );
};

export default withRouter(EditSystemWebhook);
